// Shared dialogs: name/rename, upload, upload progress, import, export,
// record, and video-export. Merged into `translations` by ../i18n.js.
export const dialogs = {
  en: {
    nameDialog: {
      save: "Save",
      cancel: "Cancel",
    },
    uploadDialog: {
      title: "Upload recording",
      prompt: "Drag and drop an audio file",
      chooseFile: "Choose file",
      maxSizeAndFormats: (sizeMB, formats) => `${sizeMB}MB max • ${formats}`,
    },
    uploadProgress: {
      phases: {
        creating: "Creating secure upload…",
        uploading: "Uploading file…",
        validating: "Validating upload…",
        cancelling: "Cancelling upload…",
        completed: "Upload complete",
      },
      progressLabel: "File upload progress",
      cancelUpload: "Cancel upload",
    },
    importDialog: {
      title: "Import transcription",
      description: "Select unassigned transcriptions to add to this project.",
      empty: "No unassigned transcriptions.",
      columnName: "Name",
      columnLanguage: "Language",
      columnDuration: "Duration",
      cancel: "Cancel",
      importButton: (n) => (n > 0 ? `Import (${n})` : "Import"),
      importingLabel: "Importing…",
      importedToast: (n) => (n === 1 ? "Imported 1 transcription" : `Imported ${n} transcriptions`),
    },
    exportDialog: {
      title: "Export transcript",
      preview: "Preview",
      formatSelectLabel: "Export format",
      languagesHeading: "Languages",
      includeHeading: "Include",
      timestamps: "Timestamps",
      languageLabels: "Language labels",
      fixedTimestampsHint: (format) => `${format} carries its own timings, so timestamps are always included.`,
      multiLangHint: "Multiple languages selected — each line is labeled automatically.",
      languageStyleName: "Language name",
      languageStyleCode: "Language code",
      cancel: "Cancel",
      download: (format) => `Download ${format}`,
      exportedToast: (fullFileName) => `Exported ${fullFileName}`,
    },
    recordDialog: {
      micLabel: "Microphone",
      systemDefaultMic: "System default",
      micFallback: (n) => `Microphone ${n}`,
      nameLabel: "Name",
      tapToStart: "Tap to start recording",
      play: "Play",
      pause: "Pause",
      playbackSpeed: "Playback speed",
      startRecording: "Start recording",
      resume: "Resume",
      end: "End",
      reRecord: "Re-record",
      submit: "Submit",
      uploadingLabel: "Uploading…",
      uploadingTitle: "Uploading recording",
      recordAudioTitle: "Record audio",
      micDenied: "Microphone access denied.",
    },
    videoExport: {
      rendering: "Rendering your video…",
      renderingDescription: "Captions are being burned in. This usually takes a couple of minutes.",
      notifyMeWhenReady: "Notify me when it's ready",
      ready: "Your video is ready",
      readyDescription: "Captions have been burned in successfully.",
      preparingDownload: "Preparing download…",
      downloadVideo: "Download video",
      failed: "Export failed",
      failedDescription: "Something went wrong while rendering your video.",
      tryAgain: "Try again",
    },
  },
  ar: {
    nameDialog: {
      save: "حفظ",
      cancel: "إلغاء",
    },
    uploadDialog: {
      title: "رفع تسجيل",
      prompt: "اسحب وأفلت ملفًا صوتيًا",
      chooseFile: "اختيار ملف",
      maxSizeAndFormats: (sizeMB, formats) => `بحد أقصى ${sizeMB} ميغابايت • ${formats}`,
    },
    uploadProgress: {
      phases: {
        creating: "جارٍ إنشاء رفع آمن…",
        uploading: "جارٍ رفع الملف…",
        validating: "جارٍ التحقق من الرفع…",
        cancelling: "جارٍ إلغاء الرفع…",
        completed: "اكتمل الرفع",
      },
      progressLabel: "تقدم رفع الملف",
      cancelUpload: "إلغاء الرفع",
    },
    importDialog: {
      title: "استيراد نص مفرّغ",
      description: "اختر النصوص المفرّغة غير المرتبطة بمشروع لإضافتها إلى هذا المشروع.",
      empty: "لا توجد نصوص مفرّغة غير مرتبطة بمشروع.",
      columnName: "الاسم",
      columnLanguage: "اللغة",
      columnDuration: "المدة",
      cancel: "إلغاء",
      importButton: (n) => (n > 0 ? `استيراد (${n})` : "استيراد"),
      importingLabel: "جارٍ الاستيراد…",
      importedToast: (n) => `تم استيراد ${n} نص مفرّغ`,
    },
    exportDialog: {
      title: "تصدير النص المفرّغ",
      preview: "معاينة",
      formatSelectLabel: "تنسيق التصدير",
      languagesHeading: "اللغات",
      includeHeading: "تضمين",
      timestamps: "الطوابع الزمنية",
      languageLabels: "تسميات اللغة",
      fixedTimestampsHint: (format) => `يحتوي تنسيق ${format} على توقيته الخاص، لذا يتم تضمين الطوابع الزمنية دائمًا.`,
      multiLangHint: "تم تحديد عدة لغات — يتم تصنيف كل سطر تلقائيًا.",
      languageStyleName: "اسم اللغة",
      languageStyleCode: "رمز اللغة",
      cancel: "إلغاء",
      download: (format) => `تنزيل ${format}`,
      exportedToast: (fullFileName) => `تم تصدير ${fullFileName}`,
    },
    recordDialog: {
      micLabel: "الميكروفون",
      systemDefaultMic: "الافتراضي للنظام",
      micFallback: (n) => `الميكروفون ${n}`,
      nameLabel: "الاسم",
      tapToStart: "اضغط لبدء التسجيل",
      play: "تشغيل",
      pause: "إيقاف مؤقت",
      playbackSpeed: "سرعة التشغيل",
      startRecording: "بدء التسجيل",
      resume: "استئناف",
      end: "إنهاء",
      reRecord: "إعادة التسجيل",
      submit: "إرسال",
      uploadingLabel: "جارٍ الرفع…",
      uploadingTitle: "جارٍ رفع التسجيل",
      recordAudioTitle: "تسجيل صوت",
      micDenied: "تم رفض الوصول إلى الميكروفون.",
    },
    videoExport: {
      rendering: "جارٍ إنشاء الفيديو…",
      renderingDescription: "يتم دمج ترجمة الفيديو الآن. عادةً ما يستغرق ذلك بضع دقائق.",
      notifyMeWhenReady: "أعلمني عند الجهوزية",
      ready: "الفيديو جاهز",
      readyDescription: "تم دمج ترجمة الفيديو بنجاح.",
      preparingDownload: "جارٍ تجهيز التنزيل…",
      downloadVideo: "تنزيل الفيديو",
      failed: "فشل التصدير",
      failedDescription: "حدث خطأ أثناء إنشاء الفيديو.",
      tryAgain: "إعادة المحاولة",
    },
  },
};
